import { useEffect, useState } from "react";
import styled from "styled-components";
import { Tabs, Tab, Skeleton } from "@mui/material";
import PrepareFetch from "../helpers/PrepareFetch";
import PointsFormatUtil from "../helpers/PointFormatUtil";
import Breakpoints from "../config/Breakpoints";
import { PointItemBlock } from "./PointStatementReport";
import alertSkeleton from "../assets/icons/alertSkeleton.svg";
import plusSign from "../assets/icons/plusSign.svg";
import minusSign from "../assets/icons/minusSign.svg";
import LuckyNumberPagination from "../atom/LuckyNumberPagination";
import StyledButton from "../atom/Button";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .warningBlock {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
        height: auto;
        border-radius: .5rem;
        background: var(--color-xLightGray);
        color: var(--color-gray);
        justify-content: center;
        align-items: center;
        padding: 2rem;
        box-sizing: border-box;
    }

    .cardAnatomy {
        height: 4.5rem;
        border-radius: .5rem;
    }
`;

const StatementList = styled.ul`
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;

    @media ${Breakpoints.sm} {
        min-height: 17.685rem;
    }
`;

const CustomTabs = styled(Tabs)`
    background: var(--color-blueGrayLight);
    border-top-left-radius: .5rem;
    border-top-right-radius: .5rem;
`;

const CustomTab = styled(Tab)`
    flex: 1;
    padding-top: .875rem;
    padding-bottom: .875rem;

    & > div {
        display: flex;
        gap: 1rem;
        width: 100%;
        text-align: left;
        align-items: center;
        color: var(--color-black);

        & > strong {
            flex: 1;
            text-transform: none;
            font-size: var(--font-size-lg);
        }

        & > .quantity {
            display: flex;
            min-width: 1.5rem;
            height: 1.5rem;
            border-radius: 99px;
            text-align: center;
            color: var(--color-white);
            background: var(--color-blueGrayDark);
            justify-content: center;
            align-items: center;
            font-size: var(--font-size-xs);
            font-weight: var(--font-weight-bold);

            &.multiple {
                padding: 0 .5rem;
            }

            @media ${Breakpoints.max.xs} {
                display: none;
            }
        }
    }
`;

const ModalFooter = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    min-height: 2.5rem;

    .changeTab {
        flex: 1;

        .button {
            
        }
    }
    
    @media ${Breakpoints.max.sm} {
        flex-direction: column;

        .changeTab {
            order: 2;
            text-align: center;
        }
        .pagination {
            order: 1;
        }
    }
`;


const LuckyNumberStatement = ({promoData}) => {
    const originLabels = {
        Acumulo: 'Acúmulo',
        Resgate: 'Resgate'
    };
    const pageSize = 5;

    const [originList, setOriginList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [data, setData] = useState([]);
    const [currentBehaviorType, setCurrentBehaviorType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const loadStatement = (page) => {
        if(currentBehaviorType === '') return;

        const fetchData = PrepareFetch(`/proxy/services/apexrest/api/v1/luckynumber/promotions/${promoData.id}/extract?behaviorType=${currentBehaviorType}&page=${page}&pageSize=${pageSize}`,"GET",{
            "Content-Type": "application/json"
          });

        setLoading(true);

        try {
            fetch(fetchData.url, fetchData.options)
            .then(response => {
                if(response.status === 200) {
                    return response.json();
                } else {
                    throw new Error(response.message);
                }                
            })
            .then(json => {
                setLoading(false);
                setData(json);
                setTotalPages(Math.ceil(json.totalRecords / pageSize));
            })
            .catch(error => {
                setLoading(false);
                setError(true);

                if(process.env.VERCEL_ENV !== "production") {
                    console.log(error);
                }
            });
        } catch (error) {
            setLoading(false);
            setError(true);
            
            if(process.env.VERCEL_ENV !== "production") {
                console.log(error);
            }
        } 
    }
    
    useEffect(() => {
        let entries = [];
        Object.entries(promoData.totalLuckyNumbersByBehavior).forEach(([key, value]) => {
            entries.push({
             name: key,
             label: originLabels[key] || key,
             quantity: value
           });   
        });
        entries.sort((a,b) => b.quantity - a.quantity);
        setOriginList(entries.filter(item => item.quantity > 0));
    },[promoData]);

    useEffect(() => {
        if(originList.length > 0) {
            setCurrentBehaviorType(originList[0].name);
        }
    },[originList]);

    useEffect(() => {
        setCurrentPage(1);
        loadStatement(1);
    },[currentBehaviorType]);

    useEffect(() => {
        loadStatement(currentPage);
    },[currentPage]);

    return (
        <Container>
            {currentBehaviorType !== '' && (
                <>
                    <CustomTabs
                        value={currentBehaviorType}
                        onChange={(ev, newValue) => setCurrentBehaviorType(newValue)}
                    >
                        {originList.map((item) => (
                            <CustomTab label={
                                <div>
                                    <strong>{item.label}</strong>
                                    <span className={`quantity ${item.quantity > 99 ? 'multiple' : ''}`}>{PointsFormatUtil(item.quantity)}</span>
                                </div>
                            } value={item.name} key={`behaviorType_${item.name}`}></CustomTab>
                        ))}
                    </CustomTabs>
                </>
            )}
            {loading && (
                <>
                    <StatementList>
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            className="cardAnatomy"
                            style={{marginBottom: '1rem'}}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            className="cardAnatomy"
                            style={{marginBottom: '1rem'}}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            className="cardAnatomy"
                            style={{marginBottom: '1rem'}}
                        />
                    </StatementList>
                    <ModalFooter>
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            className="cardAnatomy"
                            style={{width: '17.5rem', height: '2.5rem'}}
                        />
                    </ModalFooter>
                </>
            )}
            {!loading && !error && (
                <>
                    {data.data?.length > 0 && (
                        <>
                            <StatementList>
                                {data.data.map((item, index) => (
                                    <PointItemBlock key={`pointItemBlock_${index}`}>
                                        <div className="symbolBlock">
                                            <div><img src={(parseInt(item.value) > 0 ? plusSign : minusSign)} alt="" /></div>
                                            <span></span>
                                        </div>
                                        <div className="infoBlock">
                                            <strong>{item.title}</strong>
                                            <span>{item.description}</span>
                                        </div>
                                        <div className="pointBlock">
                                            {item.value === 1 && <>{PointsFormatUtil(item.value)} número da sorte</>}
                                            {item.value > 1 && <>{PointsFormatUtil(item.value)} números da sorte</>}
                                        </div>
                                    </PointItemBlock>
                                ))}
                            </StatementList>
                            <ModalFooter>
                                <LuckyNumberPagination className="pagination" currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                            </ModalFooter>
                        </>
                    )}
                </>
                
            )}
            {!loading && error && (
                <div className="warningBlock">
                    <img src={alertSkeleton} alt="" />
                    Ocorreu um erro ao carregar o conteúdo.
                    <StyledButton onClick={() => setCurrentPage(1)} className="secondary small" style={{borderRadius: ".5rem"}}>
                        Tentar novamente
                    </StyledButton>
                </div>
            )}
            
        </Container>
    );
}

export default LuckyNumberStatement;